import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  LoadingOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { RootState } from "../../store";
import { TextEnum } from "../../constants/text";
import { MessageModal } from "../../constants/features/modal";
import { CustomBreadCrumb } from "../../constants/features/breadcrumb";
import {
  createPrivacyPolicy,
  getOnePrivacyPolicy,
  modifyPrivacyPolicy,
  publishPrivacyPolicy,
} from "../../actions/privacy-policy";
import {
  useAppDispatch,
  useAppParams,
  useAppSelector,
} from "../../constants/global-hooks";
import {
  useHandlePrivacyPolicyAlertMessageChange,
  useHandlePrivacyPolicyNavigateBack,
  useHandlePrivacyPolicyTextChange,
} from "../../constants/utils";
import { resetPrivacyPolicy } from "../../constants/reducers/privacy-policy.reducer";

import "./create-privacy-policy.scss";

type EditorButtonProps = {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
};

const EditorButton: React.FC<EditorButtonProps> = ({
  title,
  onClick,
  children,
}) => {
  return (
    <button
      type="button"
      className="privacy-policy-editor-button"
      title={title}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  contentEditable: boolean;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  contentEditable,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const runCommand = useCallback(
    (command: string, commandValue?: string) => {
      document.execCommand(command, false, commandValue);
      emitChange();
      editorRef.current?.focus();
    },
    [emitChange],
  );

  const insertImage = () => {
    const url = window.prompt(TextEnum.IMAGE_URL);
    if (url) {
      runCommand("insertImage", url);
    }
  };

  return (
    <div className="privacy-policy-editor">
      <div className="privacy-policy-editor-toolbar">
        <EditorButton title={TextEnum.UNDO} onClick={() => runCommand("undo")}>
          <UndoOutlined />
        </EditorButton>
        <EditorButton title={TextEnum.REDO} onClick={() => runCommand("redo")}>
          <RedoOutlined />
        </EditorButton>
        <select
          className="privacy-policy-format-select"
          defaultValue="p"
          onChange={(event) => runCommand("formatBlock", event.target.value)}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
        <EditorButton title={TextEnum.BOLD} onClick={() => runCommand("bold")}>
          <BoldOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.ITALIC}
          onClick={() => runCommand("italic")}
        >
          <ItalicOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.UNDERLINE}
          onClick={() => runCommand("underline")}
        >
          <UnderlineOutlined />
        </EditorButton>
        <label
          className="privacy-policy-color-picker"
          title={TextEnum.TEXT_COLOR}
        >
          <FontColorsOutlined />
          <input
            type="color"
            onChange={(event) => runCommand("foreColor", event.target.value)}
          />
        </label>
        <EditorButton
          title={TextEnum.ALIGN_LEFT}
          onClick={() => runCommand("justifyLeft")}
        >
          <AlignLeftOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.ALIGN_CENTER}
          onClick={() => runCommand("justifyCenter")}
        >
          <AlignCenterOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.ALIGN_RIGHT}
          onClick={() => runCommand("justifyRight")}
        >
          <AlignRightOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.BULLET_LIST}
          onClick={() => runCommand("insertUnorderedList")}
        >
          <UnorderedListOutlined />
        </EditorButton>
        <EditorButton
          title={TextEnum.NUMBERED_LIST}
          onClick={() => runCommand("insertOrderedList")}
        >
          <OrderedListOutlined />
        </EditorButton>
        <EditorButton title={TextEnum.IMAGE} onClick={insertImage}>
          <PictureOutlined />
        </EditorButton>
      </div>
      <div
        ref={editorRef}
        className="privacy-policy-editor-content"
        contentEditable={contentEditable}
        suppressContentEditableWarning
        onInput={emitChange}
      />
    </div>
  );
};

export const CreatePrivacyPolicyPage = () => {
  const params = useAppParams();
  const dispatch = useAppDispatch();
  const { privacyPolicy, text, errorMessage, privacyPolicyActionStart } =
    useAppSelector((state: RootState) => state.privacyPolicy);
  const [clicked, setClicked] = useState(false);

  const handleNavigateBack = useHandlePrivacyPolicyNavigateBack(dispatch);
  const handleAlertMessage = useHandlePrivacyPolicyAlertMessageChange(dispatch);
  const handleTextChange = useHandlePrivacyPolicyTextChange(dispatch);
  const isActivePrivacyPolicy = privacyPolicy?.status === "active";

  useEffect(() => {
    dispatch(resetPrivacyPolicy());
    if (params.id) {
      getOnePrivacyPolicy({ id: parseInt(params.id) });
    }
  }, [dispatch, params.id]);

  const handleSave = async () => {
    handleAlertMessage(null);
    if (privacyPolicy) {
      await modifyPrivacyPolicy();
    } else {
      await createPrivacyPolicy();
    }
    setClicked(true);
  };

  const handlePublish = async () => {
    handleAlertMessage(null);
    const saveResponse = privacyPolicy
      ? await modifyPrivacyPolicy()
      : await createPrivacyPolicy();
    if (!saveResponse) {
      setClicked(true);
      return;
    }
    const privacyPolicyId =
      saveResponse.data?.privacyPolicy?.id ?? privacyPolicy?.id;

    if (privacyPolicyId) {
      await publishPrivacyPolicy(privacyPolicyId);
    }
    setClicked(true);
  };

  useEffect(() => {
    if (clicked && errorMessage === null && !privacyPolicyActionStart) {
      handleNavigateBack();
    }
    setClicked(false);
  }, [clicked, errorMessage, handleNavigateBack, privacyPolicyActionStart]);

  return (
    <main className="main-page">
      <div className="privacy-policy-header">
        <CustomBreadCrumb
          title={TextEnum.PRIVACY_POLICY_PAGE_TITLE}
          onClick={handleNavigateBack}
          modifyTitle={TextEnum.MODIFY_PRIVACY_POLICY_PAGE_TITLE}
          createTitle={TextEnum.CREATE_PRIVACY_POLICY_PAGE_TITLE}
          modify={privacyPolicy ? true : false}
        />
      </div>
      <div className="privacy-policy-editor-container">
        {!isActivePrivacyPolicy && (
          <div className="privacy-policy-editor-actions">
            <button
              disabled={privacyPolicyActionStart || !text}
              className={`privacy-policy-action-button ${
                privacyPolicyActionStart || !text ? "disabled" : ""
              }`}
              onClick={handleSave}
            >
              {privacyPolicyActionStart ? (
                <LoadingOutlined />
              ) : (
                TextEnum.SAVE_BUTTON
              )}
            </button>
            <button
              disabled={privacyPolicyActionStart || !text}
              className={`privacy-policy-action-button ${
                privacyPolicyActionStart || !text ? "disabled" : ""
              }`}
              onClick={handlePublish}
            >
              {privacyPolicyActionStart ? (
                <LoadingOutlined />
              ) : (
                TextEnum.PUBLISH
              )}
            </button>
          </div>
        )}
        <RichTextEditor
          value={text}
          onChange={handleTextChange}
          contentEditable={!isActivePrivacyPolicy}
        />
      </div>
      {errorMessage && (
        <MessageModal
          title=""
          message={errorMessage}
          onCloseClick={handleAlertMessage}
        />
      )}
    </main>
  );
};
